import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import {
  ComboboxEvent,
  ComboboxSearchResult,
  FullComboboxItem,
} from '@shared/abstraction';
import { AppkitChangeDetectionRef } from '@shared/abstraction/appkit-change-detection-ref';
import { timer } from 'rxjs';
@Component({
  standalone: false,
  selector: 'gstar-combobox',
  templateUrl: './combobox.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ComboboxComponent<T> {
  private sourceOptions: T[] = [];

  @Input() set options(values: T[]) {
    this.sourceOptions = values ?? [];
    this.refreshItems();
  }

  @Input() set value(value: T) {
    this._value = value;
    this.refreshItems();
  }

  get value(): T {
    return this._value;
  }

  @Input() trackByKey!: string;
  @Input() keyProp!: string;
  @Input() valueProp!: string;
  @Input() label!: string;
  @Input() disabled?: boolean;
  @Input() required?: boolean;
  @Input() width: number | 'auto' = 'auto';

  @Output() clearValue: EventEmitter<T> = new EventEmitter();
  @Output() selectItem: EventEmitter<T> = new EventEmitter();
  @Output() filterItems: EventEmitter<T[]> = new EventEmitter();

  @ViewChild('combo', { static: false })
  combo!: AppkitChangeDetectionRef & { hidePlaceholder: boolean };

  highlightText!: string;
  private _value!: T;

  items: WritableSignal<FullComboboxItem<T>[]> = signal<FullComboboxItem<T>[]>(
    []
  );

  public clearTrigger: WritableSignal<boolean> = signal(false);

  filterResult(event: ComboboxSearchResult<FullComboboxItem<T>>): void {
    this.highlightText = event.searchStr;
    if (event.result && event.result.length > 0) {
      this.items.set(event.result);
      this.filterItems.emit(event.result);
    } else {
      this.items.set([]);
      this.filterItems.emit([]);
    }
  }

  valueChangeHandler(event: FullComboboxItem<T> | ''): void {
    if (this.isObjectEmpty(event)) {
      this.clearValue.emit();
    }
  }

  selectItemHandler(event: ComboboxEvent<T>): void {
    this.value = event.selected;
    this.selectItem.emit(event.selected);
  }

  clear(): void {
    const items = this.items().map((item: FullComboboxItem<T>) => {
      item.checked = false;
      item.focused = false;
      return item;
    });
    this.items.set(items);
    timer(0).subscribe(() => {
      this.combo.hidePlaceholder = false;
      this.combo.cdr.detectChanges();
    });
  }

  private parseToComboboxItem(options: T[]): FullComboboxItem<T>[] {
    return options.map((item: T) => {
      return {
        ...item,
        focused: false,
        checked: this.isSelected(item),
      };
    });
  }

  private isSelected(item: T): boolean {
    return (
      !!this._value &&
      !!this.valueProp &&
      item[this.valueProp as keyof T] === this._value[this.valueProp as keyof T]
    );
  }

  private refreshItems(): void {
    const transformed: FullComboboxItem<T>[] = this.parseToComboboxItem(
      this.sourceOptions
    );
    this.items.set(transformed);
  }

  private isObjectEmpty(event: FullComboboxItem<T> | ''): boolean {
    return event === '';
  }
}
